import { useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/api';
import type { Work } from '../types/api';
import { Lightbox, PageIntro, Reveal, ScrollTabs } from '../components/ui';
import { capitalize } from '../utils/text';

type Photo = { id: string; url: string; title: string; serviceId: string; workId: string };
const ALL = 'all';
const usable = (u?: string) => !!u && !/placeholder\.com/i.test(u);

/** Images insérées dans la description détaillée (HTML assaini: https uniquement). */
const contentImages = (html?: string): string[] => [...(html ?? '').matchAll(/<img[^>]+src="([^"]+)"/g)].map((m) => m[1]);

/** Photos d'une réalisation: couverture, puis images de la description détaillée et galerie (présentes dans la fiche détaillée). */
function photosOf(w: Work): Photo[] {
  const urls = [w.cover_image?.url, ...contentImages(w.contentHtml), ...(w.work_gallery ?? []).map((g) => g.gallery?.url)].filter(usable) as string[];
  return urls.map((url, i) => ({ id: `${w.id}-${i}`, url, title: capitalize(w.title), serviceId: w.service_id, workId: w.id }));
}

export default function Gallery() {
  const [works, setWorks] = useState<Work[]>([]);
  const [details, setDetails] = useState<Record<string, Work>>({});
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>('loading');
  const [filter, setFilter] = useState(ALL);
  const [open, setOpen] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    apiService.getWorks({ enable: true })
      .then((list) => {
        if (!alive) return;
        setWorks(list); setStatus('ok');
        // Les fiches détaillées apportent les photos de galerie: elles arrivent ensuite, sans bloquer l'affichage
        Promise.allSettled(list.map((w) => apiService.getWork(w.id))).then((res) => {
          if (!alive) return;
          const map: Record<string, Work> = {};
          res.forEach((r) => { if (r.status === 'fulfilled') map[r.value.id] = r.value; });
          setDetails(map);
        });
      })
      .catch(() => alive && setStatus('error'));
    return () => { alive = false; };
  }, []);

  const photos = useMemo(() => {
    const seen = new Set<string>();
    return works.flatMap((w) => photosOf(details[w.id] ?? w)).filter((p) => (seen.has(p.url) ? false : (seen.add(p.url), true)));
  }, [works, details]);

  const tabs = useMemo(() => {
    const names = new Map<string, { label: string; n: number }>();
    for (const w of works) {
      const label = w.service?.name?.trim();
      if (w.service_id && label) names.set(w.service_id, { label, n: (names.get(w.service_id)?.n ?? 0) + photos.filter((p) => p.serviceId === w.service_id).length });
    }
    return [{ id: ALL, label: `Tout (${photos.length})` }, ...[...names].filter(([, v]) => v.n > 0).sort((a, b) => b[1].n - a[1].n).map(([id, v]) => ({ id, label: v.label }))];
  }, [works, photos]);

  const shown = filter === ALL ? photos : photos.filter((p) => p.serviceId === filter);
  const viewer = useMemo(() => shown.map((p) => ({ src: p.url, alt: p.title, caption: p.title, to: `/works/${p.workId}` })), [shown]);

  return (
    <div id="content" className="text-black">
      <PageIntro eyebrow="Galerie" title="Nos chantiers en images." motif="dots" />

      <section className="bg-white pb-20 md:pb-28">
        <div className="site-container">
          {tabs.length > 2 && <ScrollTabs tabs={tabs} active={filter} onChange={setFilter} className="mb-10 md:mb-14" />}

          {status === 'loading' && <div aria-busy className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2, 3, 4, 5].map((n) => <div key={n} className="aspect-[4/3] animate-pulse bg-secondary-200" />)}</div>}
          {status === 'error' && <p className="py-10 text-lg text-ink">Impossible de charger la galerie pour le moment. Réessayez dans un instant.</p>}
          {status === 'ok' && shown.length === 0 && <p className="py-10 text-lg text-ink">Aucune photo à afficher pour le moment.</p>}

          {shown.length > 0 && (
            <ul key={filter} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {shown.map((p, i) => (
                <li key={p.id}>
                  <Reveal delay={(i % 3) * 0.06}>
                    <button onClick={() => setOpen(i)} aria-label={`Agrandir: ${p.title}`} className="group relative block aspect-[4/3] w-full cursor-zoom-in overflow-hidden bg-secondary-200 text-left">
                      <img src={p.url} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                      <span aria-hidden className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-5 pt-16 text-[0.9375rem] font-medium leading-snug text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.title}</span>
                      <span aria-hidden className="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-brand transition-transform duration-500 group-hover:scale-x-100" />
                    </button>
                  </Reveal>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <Lightbox images={viewer} index={open} onClose={() => setOpen(null)} onChange={setOpen} />
    </div>
  );
}
